import { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useCustomer } from "../../contexts/CustomerContext";
import { useDataAccess } from "../../hooks/useDataAccess";
import { getReviewStatusStyles } from "../../utils/orderUtils";

const ReviewStatusIndicator = ({ order, compact = false }) => {
    const { getEffectiveCustomerId } = useCustomer();
    const { makeCustomerApiRequest } = useDataAccess();
    const [reviewStatus, setReviewStatus] = useState("Open for Review");
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchReviewStatus = async () => {
            const customerId = getEffectiveCustomerId();
            const callId = order?.metadata?.stream_sid;

            // Only fetch for wards customer
            if (customerId !== 'wards' || !callId) {
                setIsLoading(false);
                return;
            }

            try {
                const response = await makeCustomerApiRequest(
                    `orders/${callId}/review`,
                    { method: 'GET' },
                    'wards'
                );

                setReviewStatus(response.status || "Open for Review");
            } catch (error) {
                // If 404, that's expected for non-existent reviews
                if (error.message?.includes('404') || error.message?.includes('Not Found')) {
                    setReviewStatus("Open for Review");
                } else {
                    console.error('Failed to fetch review status:', error);
                }
            } finally {
                setIsLoading(false);
            }
        };

        if (order) {
            fetchReviewStatus();
        }
    }, [order, getEffectiveCustomerId, makeCustomerApiRequest]);

    const customerId = getEffectiveCustomerId();
    const callId = order?.metadata?.stream_sid;

    // Don't show for non-wards customers
    if (customerId !== 'wards' || !callId) {
        return null;
    }

    if (isLoading) {
        return (
            <div className="p-1.5 rounded text-xs flex items-center gap-0.5 text-font-secondary justify-center">
                <Icon icon="eos-icons:loading" className="text-sm animate-spin" />
            </div>
        );
    }

    const { icon, className } = getReviewStatusStyles(reviewStatus);

    if (compact) {
        // Compact version - just icon
        return (
            <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 justify-start`} title={reviewStatus}>
                <Icon icon={icon} className={`${className}`} />
            </div>
        );
    }

    // Full version - icon + text
    return (
        <div className={`p-1.5 rounded text-xs flex items-center gap-0.5 text-font-primary justify-center ${className}`}>
            <Icon icon={icon} className="text-sm sm:text-base" />
            <span className="text-font-primary">{reviewStatus}</span>
        </div>
    );
};

export default ReviewStatusIndicator;

